import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import type {
  CarrierInfoLatestPost,
  CarrierInfoType,
} from '#app/shared/types/CarrierInfoTypes';
import type { EquipmentTypeResponseItem } from '#app/shared/types/EquipmentTypes';
import {
  type OfferStatus,
  type OfferStatusClass,
  offerStatusMap,
} from '#app/shared/types/OfferStatuses';
import type {
  BaseOffer,
  FormattedOffer,
} from '#app/shared/types/TrucklistTypes';
import {
  SentenceDateFormat,
  ShortDateFormat,
  ShortTimeZoneFormat,
  adjustDateToGmt,
  convertFromUTCString,
  getLocalTimeZone,
} from '#app/shared/utils/datetime';
import { type FormattedTrucklistResultType, TrucklistPostType } from '../types';

/**
 * Carrier relationship filter options
 */
export enum CarrierRelationshipFilter {
  ShowFavorites = 'favorite',
  HideBlocked = 'hideBlocked',
  ShowSetupOnly = 'showSetupOnly',
  NoPreference = 'noPreference',
}

export enum AuthorityType {
  Interstate = 'Interstate',
  Intrastate = 'Intrastate',
  IntrastateHazmat = 'IntrastateHazmat',
  NoPreference = 'noPreference',
}

export enum CoverageAmount {
  GE750K = 'ge-750k',
  GE1M = 'ge-1m',
  GE2M = 'ge-2m',
  NoPreference = 'noPreference',
}

export enum NumberOfTrucks {
  GE1 = 'ge-1',
  GE2 = 'ge-2',
  GE11 = 'ge-11',
  NoPreference = 'noPreference',
}

export enum NumberOfInspections {
  E0 = 'e-0',
  GE1 = 'ge-1',
  GE2 = 'ge-2',
  NoPreference = 'noPreference',
}

/**
 * Filter data for truck list.
 */
export interface TrucklistFilterData {
  /* Lane Search */
  pickupLocation: string;
  pickupCity: string;
  pickupState: string;
  pickupLatitude: number;
  pickupLongitude: number;
  pickupRadius: number;
  pickupDate: string;
  dropoffLocation: string;
  dropoffCity: string;
  dropoffState: string;
  dropoffLatitude: number;
  dropoffLongitude: number;
  dropoffRadius: number;
  equipmentTypes: EquipmentTypeResponseItem[];
  /* Other filters */
  mcNumberOrCarrierName: string;
  carrierRelationshipFilter: CarrierRelationshipFilter;
  authorityAge: number;
  authorityType: AuthorityType;
  numberOfTrucks: NumberOfTrucks;
  numberOfInspections: NumberOfInspections;
  coverageAmountBipd: CoverageAmount;
}

export const authorityAgeOptionsInMonths = [
  { value: 0 },
  { value: 3 },
  { value: 6 },
  { value: 12 },
  { value: 24 },
  { value: 36 },
  { value: 60 },
];

const DEFAULT_RADIUS = 50;

export const DefaultFilters: TrucklistFilterData = {
  /* Lane Search */
  pickupLocation: '',
  pickupCity: '',
  pickupState: '',
  pickupLatitude: 0,
  pickupLongitude: 0,
  pickupRadius: DEFAULT_RADIUS,
  pickupDate: adjustDateToGmt(new Date()).toISOString()?.split('T')[0],
  dropoffLocation: '',
  dropoffCity: '',
  dropoffState: '',
  dropoffLatitude: 0,
  dropoffLongitude: 0,
  dropoffRadius: DEFAULT_RADIUS,
  /* Other filters */
  mcNumberOrCarrierName: '',
  equipmentTypes: [],
  authorityAge: 0,
  carrierRelationshipFilter: CarrierRelationshipFilter.NoPreference,
  authorityType: AuthorityType.NoPreference,
  numberOfTrucks: NumberOfTrucks.NoPreference,
  numberOfInspections: NumberOfInspections.NoPreference,
  coverageAmountBipd: CoverageAmount.NoPreference,
};

export const getEquipmentToShow = (
  carrier: FormattedTrucklistResultType | CarrierInfoLatestPost,
) => {
  const equipmentToDisplay = carrier?.equipment_type?.join(', ');

  if (!equipmentToDisplay) return '';

  const formattedEquipment = equipmentToDisplay.replaceAll('_', ' ').split(' ');

  return formattedEquipment
    .map((word: string) => {
      return (
        (word && word[0].toUpperCase() + word.substring(1).toLowerCase()) || ''
      );
    })
    .join(' ');
};

interface CarrierWithLocation {
  pickup_city?: string | null;
  pickup_state?: string | null;
  dropoff_city?: string | null;
  dropoff_state?: string | null;
  home_city?: string | null;
  home_state?: string | null;
  pickup_matched_home?: boolean;
  dropoff_matched_home?: boolean;
  post_type?: TrucklistPostType;
}

export const formatCityState = (
  city: string | null | undefined,
  state: string | null | undefined,
) => {
  if (!city && !state) {
    return '-';
  }

  if (!city) {
    return state || '-';
  }

  if (!state) {
    return toCapitalCase(city);
  }

  return `${toCapitalCase(city)}, ${state}`;
};

//  carrier: FormattedTrucklistResultType | CarrierInfoLatestPost,

export const getLocationDisplayParts = (
  rowData: CarrierWithLocation,
  locationType: 'pickup' | 'dropoff',
  locationComponent: 'city' | 'state',
  filterData: TrucklistFilterData,
): string | undefined => {
  if (rowData.post_type === TrucklistPostType.LOCATION) {
    if (locationType === 'pickup') {
      return locationComponent === 'city'
        ? filterData?.pickupCity || undefined
        : filterData?.pickupState || undefined;
    }
    if (locationType === 'dropoff') {
      return undefined;
    }
  }

  if (locationType === 'dropoff' && rowData.dropoff_matched_home) {
    return locationComponent === 'city'
      ? rowData.home_city || undefined
      : rowData.home_state || undefined;
  }

  if (locationType === 'pickup' && rowData.pickup_matched_home) {
    return locationComponent === 'city'
      ? rowData.home_city || undefined
      : rowData.home_state || undefined;
  }

  if (rowData.post_type === TrucklistPostType.HOME_BASE) {
    return undefined;
  }

  if (locationType === 'pickup') {
    return locationComponent === 'city'
      ? rowData.pickup_city || undefined
      : rowData.pickup_state || undefined;
  }

  return locationComponent === 'city'
    ? rowData.dropoff_city || undefined
    : rowData.dropoff_state || undefined;
};

export const getCreatedDate = (createdAt: string) => {
  return createdAt ? format(parseISO(createdAt), 'MM/dd/yyyy') : '';
};

export const getCreatedDateTimestamp = (createdAt: string) => {
  return createdAt ? format(parseISO(createdAt), 'MM/dd/yyyy HH:mm') : '';
};

export const toCapitalCase = (sentence: string) => {
  if (!sentence) {
    return '';
  }
  return sentence
    .toLowerCase()
    .split(' ')
    .map((s: string) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');
};

export const formatLastInteractionTime = (
  dateString: string,
  timeZone: string,
) => {
  const zonedDate = convertFromUTCString(dateString, timeZone);

  if (isToday(zonedDate)) {
    return format(zonedDate, 'h:mm a');
  }

  if (isYesterday(zonedDate)) {
    return 'Yesterday';
  }

  return format(zonedDate, ShortDateFormat);
};

export const formatLastInteractionMinutes = (minutesAgo: number) => {
  // If shorter than 2 hours, show minutes
  if (minutesAgo < 120) {
    return `${minutesAgo} minutes ago`;
  }

  // If longer than 2 days, show hours
  if (minutesAgo < 60 * 48 * 2) {
    return `${Math.floor(minutesAgo / 60)} hours ago`;
  }

  // Otherwise, show days
  return `${Math.floor(minutesAgo / 60 / 24)} days ago`;
};

export const formatUTCDate = (
  dateString: string,
  timeZone: string,
  format: string,
) => {
  //Force the incoming date to be processed as UTC even if it is missing the 'Z' or offset at the end
  const utcDate = fromZonedTime(dateString, 'UTC');

  //Set the date to the local timezone
  return formatInTimeZone(utcDate, timeZone, format);
};

export const formatOffer = (offer: BaseOffer): FormattedOffer => {
  const pickupDate = offer.load.pickup_date;
  const pickupDateEnd = offer.load.pickup_date_end;
  const deliveryDate = offer.load.dropoff_date;
  const deliveryDateEnd = offer.load.dropoff_date_end;
  const lastInteraction = offer.date_updated || offer.date_created;
  const pickUpTimeZone = offer.load.pickup_timezone;
  const dropOffTimeZone = offer.load.dropoff_timezone;

  const currentTimeZone = getLocalTimeZone();

  return {
    loadId: offer.load.id.toString(),
    loadReferenceId: offer.load.reference_id,
    brokerName: offer.broker_carrier_rep.broker.name,
    companyName: offer.trucker.carrier.name,
    carrierId: offer.trucker.carrier.id.toString(),
    carrierMcNumber: offer.trucker.carrier.mc_number,
    equipmentType: offer.load.equipment_type,
    id: offer.id,
    pickUpCity: offer.load.pickup_city,
    pickUpState: offer.load.pickup_state,
    pickUpDate: formatUTCDate(pickupDate, pickUpTimeZone, SentenceDateFormat),
    pickUpTime: formatUTCDate(pickupDate, pickUpTimeZone, ShortTimeZoneFormat),
    pickUpTimeEnd: pickupDateEnd
      ? formatUTCDate(pickupDateEnd, pickUpTimeZone, ShortTimeZoneFormat)
      : null,
    dropOffCity: offer.load.dropoff_city,
    dropOffState: offer.load.dropoff_state,
    deliveryDate: formatUTCDate(
      deliveryDate,
      dropOffTimeZone,
      SentenceDateFormat,
    ),
    deliveryTime: formatUTCDate(
      deliveryDate,
      dropOffTimeZone,
      ShortTimeZoneFormat,
    ),
    deliveryTimeEnd: deliveryDateEnd
      ? formatUTCDate(deliveryDateEnd, dropOffTimeZone, ShortTimeZoneFormat)
      : null,
    lastInteractionTime: formatLastInteractionTime(
      lastInteraction,
      currentTimeZone,
    ),
    lastInteractionDate: lastInteraction,
    statusClass: offerStatusMap[offer.status as OfferStatus],
    backendStatus: offer.status,
    cost: `$${offer?.load?.rate_total_value?.toFixed(2)}`,
    latestAmount: offer?.amount ? `$${offer?.amount?.toFixed(2)}` : '-',
    originalAmount: offer?.original_amount,
    numCounterOffersRemaining: offer?.num_counter_offers_remaining,
    costPerMile: offer?.load?.rate_per_mile
      ? `$${offer?.load?.rate_per_mile?.toFixed(2)}/Per Mile`
      : '-/Per Mile',
    miles: offer.load.total_miles
      ? `${Math.round(offer.load.total_miles)}`
      : '-',
    headerStatus: offerStatusMap[offer.status as OfferStatus]
      ?.replace(/_/g, ' ')
      ?.toUpperCase(),
    loadDescription: offer?.load?.description,
    pickUpFcfs: offer?.load?.pickup_fcfs,
    dropOffFcfs: offer?.load?.dropoff_fcfs,
  };
};

export const equipmentToDisplay = (carrierInfo?: CarrierInfoType): string =>
  carrierInfo?.equipment_type?.[0] || '';

export const formatGooglePlace = (place: google.maps.places.PlaceResult) => {
  const addressComponents = place.address_components;
  const city = addressComponents?.find((component) =>
    component.types.includes('locality'),
  )?.long_name;
  const state = addressComponents?.find((component) =>
    component.types.includes('administrative_area_level_1'),
  )?.short_name;
  return {
    location: city && state ? `${city}, ${state}` : '',
    city: city || '',
    state: state || '',
  };
};

export const formatAuthorityAge = (value: number) => {
  if (value < 12) {
    return `${value} months or older`;
  }
  if (value === 12) {
    return `${value / 12} year or older`;
  }

  return `${value / 12} years or older`;
};

/**
 * @description Adds backend statuses to the URL
 * @param baseUrl - The base URL
 * @param OfferStatusClass - The offer statuses
 * @returns The URL with the backend statuses
 */
export function addBackendStatusesToUrl(
  baseUrl: string,
  OfferStatusClass: OfferStatusClass[],
): string {
  if (OfferStatusClass.length === 0) {
    return baseUrl;
  }

  const relevantBackendStatuses = Object.entries(offerStatusMap)
    .filter(([_, offerStatus]) => OfferStatusClass.includes(offerStatus))
    .map(([backendStatus]) => backendStatus);

  const statusParams = relevantBackendStatuses
    .map((status) => `status=${encodeURIComponent(status)}`)
    .join('&');

  return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${statusParams}`;
}
